import { useState, useMemo } from 'react'
import NewWithdrawal from 'components/NewWithdrawal'
import NewAnticipation from 'components/NewAnticipation'
import useToastContext from "@byma/shared/hooks/useToastContext";
import Head from 'next/head'
import { useRouter } from 'next/router'
import Layout from 'components/Layout'
import Statistic from '@byma/shared/components/Statistic'
import Tabs from '@byma/shared/components/Tabs'
import EventFilter from '@byma/shared/components/EventFilter'
import Loader from '@byma/shared/components/Loader'
import useAuth from '@byma/shared/hooks/useAuth'
import fetcher from '@byma/shared/helpers/fetcher'
import useSWR, { useSWRConfig } from 'swr'
import dayjs from 'dayjs'
import useFilters from '@byma/shared/hooks/useFilters'

import { receivableTypeParser, withdrawalStatusParser, anticipationStatusParser, methodParser, money } from '@byma/shared/helpers/formatters'

function PaymentMethodsOverview({perMethod}) {
    return (
        <div className="overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Método
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Transações
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor em ingressos
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Taxas Produtor
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Custos de Emissão
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Rebate
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Receita
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {perMethod?.sort((a,b) => b.total_amount_cents - a.total_amount_cents).map((item, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className="text-left font-bold text-sm">{methodParser[item.method]} {item.installments}x</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{item.count}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.order_amount_cents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.producer_amount_cents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.emission_price_cents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money((item.customer_amount_cents - item.take_amount_cents + item.producer_amount_cents ) / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.revenue/ 100)}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white text-left">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {perMethod.reduce((acc, cur) => acc += cur.count, 0)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += cur.order_amount_cents, 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += cur.producer_amount_cents, 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += cur.emission_price_cents, 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += (cur.customer_amount_cents - cur.take_amount_cents + cur.producer_amount_cents), 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += cur.revenue, 0) / 100)}
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

function Withdrawals({ withdrawals }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { mutate } = useSWRConfig()
    const { query, ...router } = useRouter()
    const { queryString } = useFilters({...query, d:'all'})
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()

    if (!withdrawals || isLoading) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                <Loader />
            </div>
        )
    }

    if (withdrawals?.result?.length === 0) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                Nenhum saque registrado
            </div>
        )
    }

    return (
        <div className="h-96 overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data Criação
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Custo
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Status
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Obs
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {withdrawals?.result?.map((withdrawal, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className={`text-sm`}>{dayjs(withdrawal.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{withdrawal.partyId.name}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{money(withdrawal.amountCents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{withdrawal.status === 'pending' || withdrawal.status === 'requested' || withdrawal.status === 'refused' ? '-' : money(withdrawal.costCents/ 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`font-bold text-left text-sm ${withdrawal.status === 'refused' ? 'text-red-500' : ''} ${withdrawal.status === 'pending' ? 'text-blue-500' : ''} ${withdrawal.status === 'requested' ? 'text-gray-500' : ''}`}>
                                    {withdrawalStatusParser[withdrawal.status]}
                                </p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{withdrawal.description}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function Receivables({ receivables }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { mutate } = useSWRConfig()
    const { query, ...router } = useRouter()
    const { queryString } = useFilters({...query, d:'all'})
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()

    if (!receivables|| isLoading) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                <Loader />
            </div>
        )
    }

    if (receivables?.result?.length === 0) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                Nenhum recebível registrado
            </div>
        )
    }



    return (
        <div className="h-96 overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data Criação
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Tipo
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {receivables?.result?.map((receivable, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className={`text-sm`}>{dayjs(receivable.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{receivable.partyId.name}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{receivableTypeParser[receivable.type]}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{money(receivable.amountCents / 100)}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                        <th className="p-3 sticky top-0 bg-white text-right">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(reportSum(receivables?.result, 'amountCents') / 100)}
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

function Anticipations({ anticipations }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { mutate } = useSWRConfig()
    const { query, ...router } = useRouter()
    const { queryString } = useFilters({...query, d:'all'})
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()

    if (!anticipations|| isLoading) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                <Loader />
            </div>
        )
    }

    if (anticipations?.result?.length === 0) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                Nenhuma antecipação registrada 
            </div>
        )
    }

    return (
        <div className="h-96 overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data Criação
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Custo
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Status
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Obs
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {anticipations?.result?.map((anticipation, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className={`text-sm`}>{dayjs(anticipation.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{anticipation.partyId.name}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{money(anticipation.amountCents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{anticipation.status === 'pending' || anticipation.status === 'requested' || anticipation.status === 'refused' ? '-' : money(anticipation.costCents/ 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`font-bold text-left text-sm ${anticipation.status === 'refused' ? 'text-red-500' : ''} ${anticipation.status === 'pending' ? 'text-blue-500' : ''} ${anticipation.status === 'requested' ? 'text-gray-500' : ''}`}>
                                    {anticipationStatusParser[anticipation.status]}
                                </p>
                            </td>
                            <td className="p-3">
                                <p className="text-left text-sm">{anticipation.description}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                {/*
                <tfoot>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white text-left">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(reportSum(anticipations?.result, 'amountCents') / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(reportSum(anticipations?.result, 'costCents') / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                    </tr>
                </tfoot>
                */}
            </table>
        </div>
    )
}

function InvoiceItems({ invoiceItems }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { mutate } = useSWRConfig()
    const { query, ...router } = useRouter()
    const { queryString } = useFilters({...query, d:'all'})
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()

    async function deleteItem(_id) {
        setIsLoading(true)
        try {
            const res = await fetch('/api/invoiceitems/delete', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Auth': token
                },
                body: JSON.stringify({ _id }),
            })
            if (res.status === 200) {
                setIsLoading(false)
                addToast('Custo/receita removido com sucesso')
                console.log(`mutating`, `/api/invoiceitems/getAll?${queryString}`)
                mutate([`/api/invoiceitems/getAll?${queryString}`, null, token])
            } else {
                throw new Error(await res.json())
            }
        } catch (error) {
            setIsLoading(false)
            addToast('Erro ao remover custo/receita', 'error')
            console.error('An unexpected error happened occurred:', error)
        }
    }
    if (!invoiceItems || isLoading) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                <Loader />
            </div>
        )
    }
    if (invoiceItems?.result?.length === 0) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                Nenhum custo registrado
            </div>
        )
    }
    return (

        <div className="h-96 overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th></th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data Criação
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Item
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Quantidade
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor unitário
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor total
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {invoiceItems?.result?.map((invoiceItem, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3 flex items-center justify-center">
                                <button onClick={() => deleteItem(invoiceItem._id)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg> 
                                </button>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{dayjs(invoiceItem.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{invoiceItem.partyId.name}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{invoiceItem.description}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{invoiceItem.quantity}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm ${invoiceItem.unitPriceCents > 0 ? 'text-green-700' : 'text-red-700'}`}>{money(invoiceItem.unitPriceCents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm ${invoiceItem.totalCents > 0 ? 'text-green-700' : 'text-red-700'}`}>{money(invoiceItem.totalCents / 100)}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th className="p-3 sticky top-0 bg-white text-right">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(invoiceItems.result.reduce((acc, cur) => acc += cur.totalCents, 0) / 100)}
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

function reportSum(report, key) {
    if (!report) return 0
    return report.reduce((acc, cur) => acc += cur[key], 0)
}

export default function Invoices() {
    const session = useAuth()
    const { query, ...router } = useRouter()
    const { token, user: currentUser } = session

    const { queryString, rawFilters } = useFilters({...query, d:'all'})

    const { data: paymentReport } = useSWR(rawFilters?.parties?.length && token ? [`/api/payments/report?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: invoiceItems } = useSWR(rawFilters?.parties?.length && token ? [`/api/invoiceitems/getAll?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: receivables } = useSWR(rawFilters?.parties?.length && token ? [`/api/receivables/getAll?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: anticipations } = useSWR(rawFilters?.parties?.length && token ? [`/api/anticipations/getAll?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: withdrawals } = useSWR(rawFilters?.parties?.length && token ? [`/api/withdrawals/getAll?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })

    const perMethod = useMemo(() => {
        if (!paymentReport) {
            return []
        }

        let tmpObj = paymentReport.result.reduce((acc, cur) => {
            const method = `${cur._id.method}-${cur._id.installments}`
            if (!acc[method]) {
                acc[method] = {
                    method: cur._id.method,
                    installments: cur._id.installments,
                    order_amount_cents: cur.order_amount_cents,
                    total_amount_cents: cur.total_amount_cents,
                    producer_amount_cents: cur.producer_amount_cents,
                    customer_amount_cents: cur.customer_amount_cents,
                    take_amount_cents: cur.take_amount_cents,
                    emission_price_cents: cur.emission_price_cents,
                    count: cur.count,
                    revenue: cur.order_amount_cents - cur.producer_amount_cents + (cur.customer_amount_cents - cur.take_amount_cents + cur.producer_amount_cents) - cur.emission_price_cents,
                }
                return acc
            }
            acc[method].order_amount_cents += cur.order_amount_cents
            acc[method].total_amount_cents += cur.total_amount_cents
            acc[method].producer_amount_cents += cur.producer_amount_cents
            acc[method].customer_amount_cents += cur.customer_amount_cents
            acc[method].emission_price_cents += cur.emission_price_cents
            acc[method].take_amount_cents += cur.take_amount_cents
            acc[method].count += cur.count
            acc[method].revenue += cur.order_amount_cents - cur.producer_amount_cents + (cur.customer_amount_cents - cur.take_amount_cents + cur.producer_amount_cents) - cur.emission_price_cents
            return acc
        }, {})

        return Object.keys(tmpObj).map(k => tmpObj[k])
    }, [paymentReport])

    const costsCents = useMemo(() => (-1 * reportSum(invoiceItems?.result, 'totalCents')) + reportSum(anticipations?.result.filter(o => ['refused'].indexOf(o.status) < 0), 'costCents') + reportSum(withdrawals?.result.filter(o => ['refused'].indexOf(o.status) < 0), 'costCents'), [invoiceItems, anticipations, withdrawals])

    if (!rawFilters.parties?.length) {
        return (
            <Layout {...{session}}>
                <div className="p-3">
                    <div className="flex md:items-center space-x-3 mb-6 overflow-y-auto py-3">
                        <EventFilter token={token} filteredEvents={rawFilters.parties} />
                    </div>
                    Selecione um ou mais eventos
                </div>
            </Layout>
        )
    }

    const ignoredMethods = ['cash', 'credit_external', 'debit_external', 'free']

    const remainingTotalCents = (reportSum(perMethod.filter(i => ignoredMethods.indexOf(i.method) < 0 ), 'revenue') - reportSum(perMethod.filter(i => i.method.toLowerCase() === 'cash'), 'emission_price_cents') - reportSum(anticipations?.result.filter(o => ['pending', 'requested'].indexOf(o.status) >= 0), 'amountCents') - reportSum(receivables?.result, 'amountCents') - costsCents - reportSum(perMethod.filter(i => i.method.toLowerCase() === 'cash'), 'producer_amount_cents'))

    const availableToWithdrawalCents = (reportSum(receivables?.result, 'amountCents') - reportSum(withdrawals?.result.filter(o => ['refused'].indexOf(o.status) < 0), 'amountCents') )

    return (
        <Layout {...{session}}>
            <div className="p-3">
                <div className="flex md:items-center space-x-3 mb-3 overflow-y-auto py-3">
                    <EventFilter token={token} filteredEvents={rawFilters.parties} />
                </div>
                <div className="p-3 rounded box w-full text-gray-700 mb-6 flex space-x-3 items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
  <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
</svg>

                    <div>
                        <p className="tracking-tight font-bold">Atenção!</p>
                        <p>
                            Valores e dados descritos aqui não são definitivos até o fechamento do evento e podem passar por alterações a qualquer momento
                        </p>
                    </div>
                </div>


                <div className="flex flex-col md:flex-row justify-between md:space-x-3 mb-3">
                    <div className="grid grid-cols-2 gap-1 box p-3 w-full">
                        <div className="">
                            <p className="mr-3 tracking-tighter">Total em vendas</p>
                            <p className="text-4xl font-bold tracking-tighter">{money(reportSum(perMethod, 'revenue') / 100)}</p>
                        </div>
                        <div className="">
                            <p className="mr-3 tracking-tighter">Vendas via Byma</p>
                            <p className="text-xl font-bold tracking-tighter">
                                {money(reportSum(perMethod.filter(i => ignoredMethods.indexOf(i.method) < 0), 'revenue') / 100)}
                            </p>
                        </div>
                        <div className="">
                            <p className="mr-3 tracking-tighter">Total remanescente</p>
                            <p className="text-xl font-bold tracking-tighter">
                                {money(remainingTotalCents / 100)}
                            </p>
                        </div>
                        <div className="">
                            <p className="mr-3 tracking-tighter">Disponível para saque</p>
                            <p className="text-xl font-bold tracking-tighter">
                                {money(availableToWithdrawalCents / 100) }
                            </p>
                        </div>
                        <div className="">
                            <NewAnticipation 
                                availableCents={remainingTotalCents}
                                disabled={remainingTotalCents <= 0}
                            />
                        </div>
                        <div className="">
                            <NewWithdrawal 
                                availableCents={availableToWithdrawalCents}
                                disabled={availableToWithdrawalCents <= 0}
                            />
                        </div>
                    </div>
                    <div className="box p-3 w-full flex flex-col justify-between">
                        <div className="">
                            <p className="mr-3 tracking-tighter">Custos totais</p>
                            <p className="text-2xl font-bold tracking-tighter">
                                {money(costsCents / 100)}
                            </p>
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <p className="tracking-tighter">Taxas produtor</p>
                                <p className="text-xl font-bold tracking-tighter">
                                    {money((reportSum(perMethod, 'producer_amount_cents') + reportSum(perMethod, 'emission_price_cents')) / 100) }
                                </p>
                            </div>
                            <div className="flex items-center justify-between">
                                <p className="tracking-tighter">Outros custos e receitas</p>
                                <p className="text-xl font-bold tracking-tighter">
                                    {reportSum(invoiceItems?.result, 'totalCents') > 0 ? '+' :'' }
                                    {money(reportSum(invoiceItems?.result, 'totalCents') / 100)}
                                </p>
                            </div>
                            <div className="flex items-center justify-between">
                                <p className="tracking-tighter">Custos de emissão</p>
                                <p className="text-xl font-bold tracking-tighter">
                                    {money(reportSum(perMethod, 'emission_price_cents') / 100)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>


                <div className="box">
                    <p className="text-md font-bold">Receitas e Taxas</p>
                    <div className="h-96 overflow-y-auto">
                        <PaymentMethodsOverview {...{perMethod}} />
                    </div>
                </div>
                <div className="flex flex-col space-y-3">
                    <div className="box">
                        <p className="text-md font-bold">Outros custos/receitas</p>
                        <InvoiceItems {...{invoiceItems}} />
                    </div>
                    <div className="box">
                        <p className="text-md font-bold">Antecipações</p>
                        <div className="flex items-center justify-end space-x-3 mb-3">
                            <p className="text-sm">Antecipações pendentes:</p>
                            <p className="text-sm font-bold">
                                {money(reportSum(anticipations?.result.filter(o => ['pending', 'requested'].indexOf(o.status) >= 0), 'amountCents') / 100)}
                            </p>
                        </div>
                        <Anticipations {...{anticipations}} />
                        <div className="flex items-center justify-end space-x-3 mb-3">
                            <p className="text-sm">Total antecipado:</p>
                            <p className="text-sm font-bold">
                                {money(reportSum(anticipations?.result.filter(o => ['approved'].indexOf(o.status) >= 0), 'amountCents') / 100)}
                            </p>
                        </div>
                    </div>
                    <div className="box">
                        <p className="text-md font-bold">Saques realizados</p>
                        <div className="flex items-center justify-end space-x-3 mb-3">
                            <p className="text-sm">Saques pendentes:</p>
                            <p className="text-sm font-bold">
                                {money(reportSum(withdrawals?.result.filter(o => ['requested', 'pending'].indexOf(o.status) >= 0), 'amountCents') / 100)}
                            </p>
                        </div>
                        <Withdrawals {...{withdrawals}} />
                        <div className="flex items-center justify-end space-x-3 mb-3">
                            <p className="text-sm">Total sacado:</p>
                            <p className="text-sm font-bold">
                                {money(reportSum(withdrawals?.result.filter(o => o.status === 'approved'), 'amountCents') / 100)}
                            </p>
                        </div>
                    </div>
                    <div className="box">
                        <p className="text-md font-bold">Recebíveis</p>
                        <Receivables {...{receivables}} />
                    </div>
                </div>
            </div>
        </Layout>
    )
}
